import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import io from 'socket.io-client';
import styles from '../styles/chat.module.css';
import ld from '../images/load.gif';

function Chat() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { users: currentUser } = useSelector((state) => state);
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [chat, setChat] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const userId = currentUser?.userdata?._id;

  useEffect(() => {
    if (!userId) return;

    const newSocket = io('https://chatting-club-backend.onrender.com', {
      transports: ['websocket'],
      withCredentials: true
    });
    
    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      newSocket.emit('add-user', userId);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [userId]);

  useEffect(() => {
    if (!socket || !otherUser) return;

    socket.on('get-message', (data) => {
      console.log('New message received:', data);
      
      if ((data.sender === otherUser._id && data.receiver === userId) ||
          (data.sender === userId && data.receiver === otherUser._id)) {
        
        setMessages(prev => [...prev, {
          sender: data.sender,
          text: data.text,
          createdAt: data.createdAt || new Date()
        }]);
        
        setTimeout(scrollToBottom, 100);
      }
    });

    socket.on('message-sent', (data) => {
      if (!data.success) {
        toast.warning(data.message || 'Message not delivered', {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    });

    socket.on('typing', ({ sender }) => {
      if (sender === otherUser._id) {
        setOtherUserTyping(true);
      }
    });

    socket.on('stop-typing', ({ sender }) => {
      if (sender === otherUser._id) {
        setOtherUserTyping(false);
      }
    });

    return () => {
      socket.off('get-message');
      socket.off('message-sent');
      socket.off('typing');
      socket.off('stop-typing');
    };
  }, [socket, otherUser, userId]);

  useEffect(() => {
    if (id) {
      fetchChat();
    }
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChat = async () => {
    try {
      setLoading(true);
      const response = await axios.post('https://chatting-club-backend.onrender.com/api/chats/get-chat', 
        { chatId: id },
        { withCredentials: true }
      );

      if (response.data.success) {
        const chatData = response.data.chat;
        setChat(chatData);
        
        const other = chatData.users.find(
          user => user._id !== userId
        );
        setOtherUser(other);
        
        if (chatData.messages) {
          setMessages(chatData.messages);
        }
      } else {
        toast.error(response.data.message || 'Failed to load chat', {
          position: 'bottom-right',
          theme: 'colored'
        });
        navigate('/chats');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
      navigate('/chats');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      toast.warning('Please enter a message', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return;
    }

    if (!otherUser) {
      toast.error('User not found', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return;
    }

    if (!socketRef.current || !isConnected) {
      toast.error('Not connected to server', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return;
    }

    try {
      setSending(true);
      const messageText = newMessage.trim();
      
      const response = await axios.post('https://chatting-club-backend.onrender.com/api/messages/new-message', {
        chatId: id,
        sender: userId,
        text: messageText
      });

      if (response.data.success) {
        const newMsg = {
          sender: userId,
          text: messageText,
          createdAt: new Date()
        };
        setMessages(prev => [...prev, newMsg]);
        setNewMessage('');

        socketRef.current.emit('send-message', {
          sender: userId,
          receiver: otherUser._id,
          text: messageText,
          chatId: id
        });

        handleTyping(false);
      } else {
        toast.error(response.data.message || 'Failed to send message', {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send message';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
    } finally {
      setSending(false);
    }
  };

  const handleTyping = (isTypingNow) => {
    if (!socketRef.current || !otherUser || !isConnected) return;

    if (isTypingNow) {
      socketRef.current.emit('typing', { 
        sender: userId, 
        receiver: otherUser._id 
      });
    } else {
      socketRef.current.emit('stop-typing', { 
        sender: userId, 
        receiver: otherUser._id 
      });
    }
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setNewMessage(text);

    if (text.trim().length > 0) {
      if (!isTyping) {
        setIsTyping(true);
        handleTyping(true);
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        handleTyping(false);
      }, 1500);
    } else {
      setIsTyping(false);
      handleTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRandomColor = (name) => {
    const colors = [
      '#9F7BFF', '#FF6B9D', '#4ADE80', '#60A5FA',
      '#FBBF24', '#F472B6', '#34D399', '#A78BFA',
      '#FB923C', '#22D3EE', '#F87171', '#818CF8'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return d.toLocaleDateString();
  };

  const groupMessagesByDate = () => {
    const groups = {};
    messages.forEach(msg => {
      const date = new Date(msg.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(msg);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate();

  if (loading) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.loadingContainer}>
          <img src={ld} className={styles.loaderImage} alt="Loading chat..." />
          <p className={styles.loadingText}>Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatContainer}>
      <div className={styles.chatHeader}>
        <button className={styles.backButton} onClick={() => navigate('/chats')}>
          ← Back
        </button>
        <div className={styles.headerInfo}>
          <div 
            className={styles.headerAvatar}
            style={{ backgroundColor: getRandomColor(otherUser?.name || '') }}
          >
            {getInitials(otherUser?.name || '')}
          </div>
          <div className={styles.headerDetails}>
            <h3 className={styles.headerName}>{otherUser?.name || 'User'}</h3>
          </div>
        </div>
      </div>

      <div className={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div className={styles.noMessages}>
            <div className={styles.noMessagesIcon}>💬</div>
            <p className={styles.noMessagesText}>No messages yet</p>
            <p className={styles.noMessagesSub}>Start the conversation with {otherUser?.name || 'User'}</p>
          </div>
        ) : (
          <div className={styles.messagesList}>
            {Object.keys(messageGroups).map((dateKey) => (
              <div key={dateKey}>
                <div className={styles.dateDivider}>
                  <span>{formatDate(new Date(dateKey))}</span>
                </div>
                {messageGroups[dateKey].map((msg, index) => {
                  const isOwnMessage = msg.sender === userId;
                  return (
                    <div
                      key={index}
                      className={`${styles.messageWrapper} ${isOwnMessage ? styles.ownMessage : styles.otherMessage}`}
                    >
                      {!isOwnMessage && (
                        <div 
                          className={styles.messageAvatar}
                          style={{ backgroundColor: getRandomColor(otherUser?.name || '') }}
                        >
                          {getInitials(otherUser?.name || '')}
                        </div>
                      )}
                      <div className={styles.messageBubble}>
                        <p className={styles.messageText}>{msg.text}</p>
                        <span className={styles.messageTime}>
                          {formatTime(msg.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {otherUserTyping && (
              <div className={styles.typingIndicator}>
                <span>{otherUser?.name} is typing</span>
                <span className={styles.typingDots}>...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form className={styles.inputContainer} onSubmit={sendMessage}>
        <input
          type="text"
          className={styles.messageInput}
          placeholder={isConnected ? "Type a message..." : "Connecting..."}
          value={newMessage}
          onChange={handleInputChange}
          disabled={sending || !isConnected}
        />
        <button 
          type="submit" 
          className={styles.sendButton}
          disabled={sending || !newMessage.trim() || !isConnected}
        >
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default Chat;